/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Spacer, TextField } from 'sarsaparilla';

import { licensePlateValidator } from 'ui-permit/shared-components/utils/format';

const VEHICLE_INFORMATION_PROP_PATH = 'vehicle_information';
const DRIVER_LISENSE_PROP_PATH = 'driver_license';

function RegVehicleInformation({
    reservation,
    handleVehicleDetailsChange,
    driverLicenseRequired,
    addToInputRefs,
    vIndex,
    enforceValidation,
}) {
    const state = get(reservation, `${VEHICLE_INFORMATION_PROP_PATH}.state`, '');
    const licenseNum = get(
        reservation,
        `${VEHICLE_INFORMATION_PROP_PATH}.license_plate_number`,
        ''
    );
    const year = get(reservation, `${VEHICLE_INFORMATION_PROP_PATH}.year`, '');
    const make = get(reservation, `${VEHICLE_INFORMATION_PROP_PATH}.make`, '');
    const model = get(reservation, `${VEHICLE_INFORMATION_PROP_PATH}.model`, '');
    const color = get(reservation, `${VEHICLE_INFORMATION_PROP_PATH}.color`, '');
    const driverLicenseNo = get(reservation, DRIVER_LISENSE_PROP_PATH, '');

    let driverLicenseComponent = null;
    if (driverLicenseRequired) {
        driverLicenseComponent = (
            <div className="rec-grid-4-12">
                <div className="rec-form-item-wrap">
                    <TextField
                        id="driver-license"
                        label="Driver License #"
                        ref={addToInputRefs(vIndex + 12)}
                        value={driverLicenseNo}
                        isRequired
                        onChange={(e) =>
                            handleVehicleDetailsChange(
                                `${DRIVER_LISENSE_PROP_PATH}`,
                                e.target.value
                            )
                        }
                    />
                </div>
            </div>
        );
    }
    return (
        <div className="permit-vehicle-wrap">
            <div className="rec-nested-wrap">
                <div className="rec-grid-12-12">
                    <h1 className="form-section-header">Vehicle Information</h1>
                    <p>
                        Enter the type of vehicle you will be using for your permit. Be
                        sure to review the location&apos;s permit regulations to confirm
                        you have an approved vehicle.
                        <Spacer size="sm" />
                        If the location allows rental vehicles, please wait to purchase
                        this permit until you have your vehicle information. <br />
                        If any vehicle information fields are not applicable to your
                        approved vehicle, please refer to the location&apos;s guidance on
                        what should be entered or type N/A.
                    </p>
                </div>
                {driverLicenseComponent}
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-state"
                            label="State"
                            ref={addToInputRefs(vIndex + 13)}
                            value={state}
                            isRequired={enforceValidation}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.state`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-license-num"
                            label="License Plate #"
                            ref={addToInputRefs(vIndex + 14)}
                            value={licenseNum}
                            isRequired={enforceValidation}
                            validator={licensePlateValidator}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.license_plate_number`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-year"
                            label="Year"
                            ref={addToInputRefs(vIndex + 15)}
                            value={year}
                            isRequired={enforceValidation}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.year`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-make"
                            aria-label="vehicle make"
                            label="Make"
                            ref={addToInputRefs(vIndex + 16)}
                            value={make}
                            isRequired={enforceValidation}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.make`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-model"
                            aria-label="vehicle model"
                            label="Model"
                            ref={addToInputRefs(vIndex + 17)}
                            value={model}
                            isRequired={enforceValidation}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.model`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
                <div className="rec-grid-4-12">
                    <div className="rec-form-item-wrap">
                        <TextField
                            id="vehicle-color"
                            aria-label="vehicle color"
                            label="Color"
                            ref={addToInputRefs(vIndex + 18)}
                            value={color}
                            isRequired={enforceValidation}
                            onChange={(e) =>
                                handleVehicleDetailsChange(
                                    `${VEHICLE_INFORMATION_PROP_PATH}.color`,
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

RegVehicleInformation.propTypes = {
    reservation: PropTypes.object.isRequired,
    validation: PropTypes.object.isRequired,
    handleVehicleDetailsChange: PropTypes.func.isRequired,
    onFormValidate: PropTypes.func,
    driverLicenseRequired: PropTypes.bool,
    enforceValidation: PropTypes.bool,
    displayValidationError: PropTypes.bool,
};

RegVehicleInformation.defaultProps = {
    driverLicenseRequired: false,
    enforceValidation: false,
};

export default RegVehicleInformation;
